import { interleaveArrays } from '@/utils/arrayUtils';
import { type PromotedEvent, type RelatedEventsData, type RelatedEvent, type UrgencySignals } from './types';
import { transformPromotedEventsToRelatedEvents } from '@/utils/mappers';
import type { EventInteraction, EventInteractionWithContext } from '@/tracking/types';

/**
 * Merges related events and promoted events into a single RelatedEventsData object
 * with interleaved ordering
 *
 * @param relatedEvents - Related events data
 * @param promotedEvents - Array of promoted events
 * @returns Merged RelatedEventsData object or null if both are null/empty
 */
export function mergeRelatedAndPromotedEvents(
  relatedEvents: RelatedEventsData | null,
  promotedEvents: PromotedEvent[] | null,
): RelatedEventsData | null {
  if ((!relatedEvents && !promotedEvents) || (relatedEvents?.events?.length === 0 && promotedEvents?.length === 0)) {
    return null;
  }

  const relatedEventsList = relatedEvents?.events || [];
  const promotedEventsList = transformPromotedEventsToRelatedEvents(promotedEvents);
  const interleavedEvents = interleaveArrays(promotedEventsList, relatedEventsList);

  return {
    events: interleavedEvents,
    unrecoverableEventIds: relatedEvents?.unrecoverableEventIds || [],
  };
}

/**
 * Computes event ranks for tracking purposes
 *
 * Due to historical reasons, we compute both relative and absolute ranks:
 * - rank: Position within the same related event type (promoted vs organic)
 * - absoluteRank: Position within the entire list
 *
 * @param events - Array of all events
 * @param eventId - ID of the event to find
 * @param isPromoted - Whether the event is promoted
 * @returns Object with rank and absoluteRank
 */
export function computeEventRanks(
  events: RelatedEvent[],
  eventId: string,
  isPromoted: boolean | undefined,
): { rank: number; absoluteRank: number } {
  if (!eventId || typeof isPromoted === 'undefined') {
    return {
      rank: 0,
      absoluteRank: 0,
    };
  }

  const eventsGroup = events.filter((e) => isPromoted === e.isPromoted);

  return {
    rank: eventsGroup.findIndex((e) => e.id === eventId) + 1,
    absoluteRank: events.findIndex((e) => e.id === eventId) + 1,
  };
}

/**
 * Converts urgency signals to tracking format
 *
 * @param urgencySignals - Urgency signals from event data
 * @returns Array of urgency signal strings for tracking
 */
export function getUrgencySignalsForTracking(urgencySignals?: UrgencySignals): string[] {
  if (!urgencySignals) {
    return [];
  }

  const signals: string[] = [];

  if (urgencySignals.new) signals.push('new');
  if (urgencySignals.salesEndSoon) signals.push('salesEndSoon');
  if (urgencySignals.fewTickets) signals.push('fewTickets');
  if (urgencySignals.popular) signals.push('popular');
  if (urgencySignals.goingFast) signals.push('goingFast');

  return signals;
}

/**
 * Creates tracking context for related events interactions
 * This function generates the tracking context that gets added to each interaction.
 *
 * @param mainEvent - The main event
 * @param allEvents - Array of all related events for rank calculation
 * @param locale - The locale of the user
 * @returns Function that transforms event interactions into tracking context
 */
export const createTrackingInformationGetter = (
  mainEvent: {
    id?: string;
    isOnline?: boolean;
    isEnded?: boolean;
  },
  allEvents: RelatedEvent[] = [],
  locale: string,
  sessionInfo: {
    userId?: string | null;
    sessionId?: string | null;
    guestId?: string | null;
  },
) => {
  return (eventInteraction: EventInteraction): EventInteractionWithContext => {
    // Find the specific event from the events array for rank calculation
    const eventToTrack = allEvents.find(
      (e) => e.id === eventInteraction.id && e.isPromoted === eventInteraction.event.isPromoted,
    );

    if (!eventToTrack) {
      console.error(`Event with id ${eventInteraction.id} not found in related events list`);
      return {} as EventInteractionWithContext;
    }

    // Calculate ranks
    const { rank, absoluteRank } = computeEventRanks(allEvents, eventInteraction.id, eventToTrack.isPromoted);

    const hasOpenPromotion = eventToTrack.hasOpenDiscount ?? false;
    const hasBOGOLabel = eventToTrack.specialDiscounts?.hasBogoTickets ?? false;
    const urgencySignals = getUrgencySignalsForTracking(eventToTrack.urgencySignals);

    return {
      id: eventInteraction.id,
      action: eventInteraction.action,
      event: eventInteraction.event,
      user: {
        id: sessionInfo.userId ?? undefined,
        locale,
        guestId: sessionInfo.guestId ?? undefined,
        sessionId: sessionInfo.sessionId ?? undefined,
      },
      adId: eventToTrack.promotedListingMetadata?.adId,
      place: {
        name: 'related_events',
        // Since we don't have pagination, page.number is always 0
        position: rank, // For page 0, use rank (like EB-UI)
        absoluteRank,
        page: 1, // We don't have carousel pagination, so always page 1
        context: {
          related_event_id: mainEvent.id,
          is_online: mainEvent.isOnline,
        },
        tabKey: '',
        subInterface: {
          name: mainEvent.isEnded ? 'event_expired_page' : 'events_carousel',
          position: '',
        },
        placementId: eventToTrack.promotedListingMetadata?.placementId,
      },
      displayContext: {
        hasOpenPromotion,
        hasBOGOLabel,
        urgencySignals,
      },
      venueId: eventToTrack.venue?.id,
    };
  };
};
